export default defineNuxtPlugin(() => {
  const COOKIE_CONSENT_KEY = "tayba-cookie-consent";
  const CONSENT_EVENT = "tayba-cookie-consent-changed";
  const config = useRuntimeConfig();
  const gaId = config.public.GA_MEASUREMENT_ID as string;
  const fbPixelId = config.public.FB_PIXEL_ID as string;

  const pushToDataLayer = (...args: any[]) => {
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push(args);
  };

  const loadScriptOnce = (src: string, id: string) => {
    if (document.getElementById(id)) return;
    const script = document.createElement("script");
    script.id = id;
    script.async = true;
    script.src = src;
    document.head.appendChild(script);
  };

  const initGA = () => {
    if (!gaId) return;

    // Load GA4 script directly — no Consent Mode needed since our cookie banner
    // already gates this function (only called when user accepted analytics cookies)
    loadScriptOnce(
      `https://www.googletagmanager.com/gtag/js?id=${gaId}`,
      "ga-gtag",
    );

    // Set up gtag if not already present (GTM may have set it up already)
    // IMPORTANT: must use `arguments` (not rest params) so GA4 can detect
    // IArguments objects in the dataLayer queue and process them correctly.
    (window as any).dataLayer = (window as any).dataLayer || [];
    if (!(window as any).gtag) {
      (window as any).gtag = function () {
        // eslint-disable-next-line prefer-rest-params
        (window as any).dataLayer.push(arguments);
      };
    }

    (window as any).gtag("js", new Date());
    (window as any).gtag("config", gaId, { anonymize_ip: true });
  };

  const initFBPixel = () => {
    if (!fbPixelId) return;
    if ((window as any).fbq) return;

    // Set up the fbq stub queue (same pattern as the official snippet but without
    // the DOM insertion — we use loadScriptOnce to append to <head>, identical
    // to how GA4 is loaded, avoiding insertBefore(t, firstScript) fragility).
    const n: any = ((window as any).fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    });
    if (!(window as any)._fbq) (window as any)._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];

    loadScriptOnce(
      "https://connect.facebook.net/en_US/fbevents.js",
      "fb-fbevents",
    );

    (window as any).fbq("init", fbPixelId);
    (window as any).fbq("track", "PageView");
  };

  const deleteCookie = (name: string) => {
    const hostname = window.location.hostname;
    const domains = [hostname, `.${hostname}`];
    domains.forEach((domain) => {
      document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}`;
    });
    document.cookie = `${name}=; Max-Age=0; path=/`;
  };

  const clearAnalyticsCookies = () => {
    const cookieNames = document.cookie
      .split(";")
      .map((c) => c.trim().split("=")[0] ?? "")
      .filter(Boolean);

    cookieNames
      .filter((name) => name.startsWith("_ga") || name.startsWith("_gid"))
      .forEach(deleteCookie);

    ["_fbp", "_fbc"].forEach(deleteCookie);
  };

  const enableTracking = () => {
    initGA();
    initFBPixel();
  };

  const disableTracking = () => {
    clearAnalyticsCookies();
    if ((window as any).gtag) {
      (window as any).gtag("consent", "update", {
        ad_storage: "denied",
        analytics_storage: "denied",
      });
    } else {
      pushToDataLayer("consent", "update", {
        ad_storage: "denied",
        analytics_storage: "denied",
      });
    }
    if ((window as any).fbq) {
      (window as any).fbq("consent", "revoke");
    }
  };

  const parseConsent = (raw: string | null) => {
    if (!raw) return null;
    if (raw === "accepted" || raw === "declined") {
      return { analytics: raw === "accepted" };
    }
    try {
      const parsed = JSON.parse(raw);
      return { analytics: !!parsed?.analytics };
    } catch (error) {
      return null;
    }
  };

  const applyConsent = (value: string | { analytics?: boolean } | null) => {
    const prefs =
      typeof value === "string" ? parseConsent(value) : value || null;
    if (prefs?.analytics) {
      enableTracking();
      return;
    }
    if (prefs && prefs.analytics === false) {
      disableTracking();
    }
  };

  // Apply consent immediately — safe in .client.ts plugins (DOM is available)
  applyConsent(localStorage.getItem(COOKIE_CONSENT_KEY));

  window.addEventListener(CONSENT_EVENT, (event: Event) => {
    const detail = (event as CustomEvent).detail as {
      value?: string | { analytics?: boolean };
    } | null;
    applyConsent(detail?.value ?? null);
  });
});
